import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { PlanetPositions } from "@/types";
import { ApiDate } from "@/types/Apis";
import { Halfsum } from "@/types/Halfsum";
import { Definitions } from "@/types/Definition";
import type { BirthRecordLite, ChartSlotData } from "@/types/BirthRecord";

/**
 * Which saved record (or event) a chart slot was loaded from / saved as.
 * `snapshot` holds the record's values at that moment and is the baseline the
 * dirty check compares against — wall-clock ints + place/coords only, never
 * Date objects, so persisting it round-trips exactly (no ISO-string drift).
 */
export interface ChartRecordRef {
	id: string;
	name: string;
	eventId?: string;
	eventLabel?: string;
	snapshot: ChartSlotData;
}

/** A recently loaded/saved record, cached whole so the pre-compute recents
 *  strip can re-load it without resolving ids against the paginated list. */
export interface RecentChart {
	id: string;
	name: string;
	data: ChartSlotData;
	at: number;
}

interface BirthInfo {
	birthDateTime: Date | null;
	longitude: number;
	latitude: number;
	place: string;
	birthDateTimeInfo: ApiDate | undefined;
}

interface PresentInfo {
	presentDateTime: Date | null;
	presentLongitude: number;
	presentLatitude: number;
	presentPlace: string;
	presentDateTimeInfo: ApiDate | undefined;
}

interface PlanetData {
	natalPlanets: PlanetPositions | undefined;
	directedPlanets: PlanetPositions | undefined;
	transitPlanets: PlanetPositions | undefined;
}

interface SearchData {
	searchTerm: string;
	searchedHalfsums: Definitions[];
	searchedFormulas: Definitions[];
}

interface UIState {
	drawHalfsum: Halfsum[];
	// The rotating dial pointer's angle, in radians, exactly as applied to the
	// #dial-com transform. The single source of truth for dial rotation (ARCH-1).
	// Ephemeral UI state, so it is intentionally left out of `partialize`.
	dialAngleRad: number;
	// A monotonic counter the dials watch to re-project tracked halfsums onto the
	// current planets. Bumped by every producer of the "planets changed, resync
	// the halfsum angles" signal — compute/refresh, loading a saved chart, adding
	// a halfsum, "use now" — so no host has to thread a refresh callback (the same
	// single-source move ARCH-1 made for dialAngleRad). A nonce, not a boolean, so
	// the dials never need to reset it. Ephemeral, so left out of `partialize`.
	halfsumRefreshNonce: number;
}

interface IdentityState {
	// The one chart name — shared by the input forms, the saved-charts panel,
	// and ShareDialog (was four page-local useStates named twice over).
	chartLabel: string;
	// The present/transit twin of chartLabel — the name the transit chip's
	// "Save moment" writes when it bookmarks a solar-arc moment as an event.
	// Present-scoped (cleared by resetPresentInfo), never the birth chart's name.
	momentLabel: string;
	birthRecordRef: ChartRecordRef | null;
	presentRecordRef: ChartRecordRef | null;
	recents: RecentChart[];
	// A birth-slot load that is waiting on the user to confirm discarding unsaved
	// edits to the current chart. Set when a load is requested while the birth
	// slot is dirty; the discard-edits dialog reads it. Transient (never persisted).
	pendingBirthLoad: BirthRecordLite | null;
	// A "New chart" reset waiting on the user to confirm discarding unsaved edits.
	// Set when New chart is pressed while the birth slot is dirty; the discard
	// dialog reads it. Transient (never persisted).
	pendingNewChart: boolean;
}

interface ChartState
	extends BirthInfo,
		PresentInfo,
		PlanetData,
		SearchData,
		UIState,
		IdentityState {
	// Birth info actions
	setBirthDateTime: (d: Date | null) => void;
	setLongitude: (d: number) => void;
	setLatitude: (d: number) => void;
	setPlace: (d: string) => void;
	setBirthDateTimeInfo: (d: ApiDate | undefined) => void;

	// Present info actions
	setPresentDateTime: (d: Date | null) => void;
	setPresentLongitude: (d: number) => void;
	setPresentLatitude: (d: number) => void;
	setPresentPlace: (d: string) => void;
	setPresentDateTimeInfo: (d: ApiDate | undefined) => void;

	// Planet data actions
	setNatalPlanets: (d: PlanetPositions | undefined) => void;
	setDirectedPlanets: (d: PlanetPositions | undefined) => void;
	setTransitPlanets: (d: PlanetPositions | undefined) => void;
	// Clear all computed positions at once — returns every dial host to its input
	// card. Used by "New chart" so the stale chart leaves the dial.
	clearComputedChart: () => void;

	// Search actions
	setSearchTerm: (d: string) => void;
	setSearchedHalfsums: (d: Definitions[]) => void;
	setSearchedFormulas: (d: Definitions[]) => void;

	// UI actions
	setDrawHalfsum: (d: Halfsum[]) => void;
	setDialAngleRad: (rad: number) => void;
	bumpHalfsumRefresh: () => void;

	// Identity actions
	setChartLabel: (d: string) => void;
	setMomentLabel: (d: string) => void;
	setBirthRecordRef: (d: ChartRecordRef | null) => void;
	setPresentRecordRef: (d: ChartRecordRef | null) => void;
	setPendingBirthLoad: (d: BirthRecordLite | null) => void;
	setPendingNewChart: (d: boolean) => void;
	pushRecent: (d: RecentChart) => void;
	removeRecent: (id: string) => void;
	renameRecent: (id: string, name: string) => void;

	// Reset actions
	resetBirthInfo: () => void;
	resetPresentInfo: () => void;
	resetAll: () => void;
	// Wipe the whole identity slice — labels, both record refs, AND the cached
	// recents. A narrower reset kept for callers that only need the identity gone.
	resetIdentity: () => void;
	// Full account-switch wipe: identity (incl. recents) PLUS the birth/present
	// slots and the computed dial. On a Clerk switch that doesn't hard-reload, an
	// identity-only reset would leave the previous user's birth data + chart on
	// screen — this clears everything derived from a person.
	resetForAccountSwitch: () => void;
}

const initialBirthInfo: BirthInfo = {
	birthDateTime: new Date(),
	longitude: 0,
	latitude: 0,
	place: "",
	birthDateTimeInfo: undefined,
};

const initialPresentInfo: PresentInfo = {
	presentDateTime: new Date(),
	presentLongitude: 0,
	presentLatitude: 0,
	presentPlace: "",
	presentDateTimeInfo: undefined,
};

const initialPlanetData: PlanetData = {
	natalPlanets: undefined,
	directedPlanets: undefined,
	transitPlanets: undefined,
};

const initialSearchData: SearchData = {
	searchTerm: "",
	searchedHalfsums: [],
	searchedFormulas: [],
};

const initialUIState: UIState = {
	drawHalfsum: [],
	dialAngleRad: 0,
	halfsumRefreshNonce: 0,
};

const initialIdentityState: IdentityState = {
	chartLabel: "",
	momentLabel: "",
	birthRecordRef: null,
	presentRecordRef: null,
	recents: [],
	pendingBirthLoad: null,
	pendingNewChart: false,
};

export const useChartStore = create<ChartState>()(
	devtools(
		persist(
			(set) => ({
				// Initial state
				...initialBirthInfo,
				...initialPresentInfo,
				...initialPlanetData,
				...initialSearchData,
				...initialUIState,
				...initialIdentityState,

				// Birth info actions
				setBirthDateTime: (d) => set({ birthDateTime: d }),
				setLongitude: (d) => set({ longitude: d }),
				setLatitude: (d) => set({ latitude: d }),
				setPlace: (d) => set({ place: d }),
				setBirthDateTimeInfo: (d) =>
					set((state) => {
						// Mid-session guard: a record ref is attached but no committed
						// ints exist yet. If what is now being committed is not the
						// ref's chart, this is a fresh entry — detach the ref so a
						// later "Update" can't overwrite the old record with a
						// different person's data. (Reload no longer persists the ref,
						// so this can't fire on first load — it's purely defensive.)
						const ref = state.birthRecordRef;
						const freshMismatch =
							d !== undefined &&
							state.birthDateTimeInfo === undefined &&
							ref !== null &&
							!(
								d.year === ref.snapshot.year &&
								d.month === ref.snapshot.month &&
								d.day === ref.snapshot.day &&
								d.hour === ref.snapshot.hour &&
								d.min === ref.snapshot.min &&
								state.place === ref.snapshot.place &&
								state.latitude === ref.snapshot.latitude &&
								state.longitude === ref.snapshot.longitude
							);
						return freshMismatch
							? { birthDateTimeInfo: d, birthRecordRef: null }
							: { birthDateTimeInfo: d };
					}),

				// Present info actions
				setPresentDateTime: (d) => set({ presentDateTime: d }),
				setPresentLongitude: (d) => set({ presentLongitude: d }),
				setPresentLatitude: (d) => set({ presentLatitude: d }),
				setPresentPlace: (d) => set({ presentPlace: d }),
				setPresentDateTimeInfo: (d) =>
					set((state) => {
						// Same mid-session fresh-commit guard as the birth slot (see above).
						const ref = state.presentRecordRef;
						const freshMismatch =
							d !== undefined &&
							state.presentDateTimeInfo === undefined &&
							ref !== null &&
							!(
								d.year === ref.snapshot.year &&
								d.month === ref.snapshot.month &&
								d.day === ref.snapshot.day &&
								d.hour === ref.snapshot.hour &&
								d.min === ref.snapshot.min &&
								state.presentPlace === ref.snapshot.place &&
								state.presentLatitude === ref.snapshot.latitude &&
								state.presentLongitude === ref.snapshot.longitude
							);
						return freshMismatch
							? { presentDateTimeInfo: d, presentRecordRef: null }
							: { presentDateTimeInfo: d };
					}),

				// Planet data actions
				setNatalPlanets: (d) => set({ natalPlanets: d }),
				setDirectedPlanets: (d) => set({ directedPlanets: d }),
				setTransitPlanets: (d) => set({ transitPlanets: d }),
				clearComputedChart: () => set({ ...initialPlanetData }),

				// Search actions
				setSearchTerm: (d) => set({ searchTerm: d }),
				setSearchedHalfsums: (d) => set({ searchedHalfsums: d }),
				setSearchedFormulas: (d) => set({ searchedFormulas: d }),

				// UI actions
				setDrawHalfsum: (d) => set({ drawHalfsum: d }),
				setDialAngleRad: (rad) => set({ dialAngleRad: rad }),
				bumpHalfsumRefresh: () =>
					set((s) => ({ halfsumRefreshNonce: s.halfsumRefreshNonce + 1 })),

				// Identity actions
				setChartLabel: (d) => set({ chartLabel: d }),
				setMomentLabel: (d) => set({ momentLabel: d }),
				setBirthRecordRef: (d) => set({ birthRecordRef: d }),
				setPresentRecordRef: (d) => set({ presentRecordRef: d }),
				setPendingBirthLoad: (d) => set({ pendingBirthLoad: d }),
				setPendingNewChart: (d) => set({ pendingNewChart: d }),
				pushRecent: (d) =>
					set((state) => ({
						recents: [d, ...state.recents.filter((r) => r.id !== d.id)].slice(0, 3),
					})),
				// Keep the cached strip in step with the bank: a deleted record must
				// not stay loadable, a renamed one must not show its old name.
				removeRecent: (id) =>
					set((state) => ({ recents: state.recents.filter((r) => r.id !== id) })),
				renameRecent: (id, name) =>
					set((state) => ({
						recents: state.recents.map((r) => (r.id === id ? { ...r, name } : r)),
					})),

				// Reset actions — a slot reset also detaches its record ref.
				// `recents` deliberately survive every reset: they are history, not
				// slot state.
				resetBirthInfo: () => set({ ...initialBirthInfo, birthRecordRef: null }),
				resetPresentInfo: () =>
					set({ ...initialPresentInfo, momentLabel: "", presentRecordRef: null }),
				resetAll: () =>
					set({
						...initialBirthInfo,
						...initialPresentInfo,
						...initialPlanetData,
						...initialSearchData,
						...initialUIState,
						chartLabel: "",
						momentLabel: "",
						birthRecordRef: null,
						presentRecordRef: null,
					}),
				// Unlike the slot resets, this also clears `recents` — history is
				// per-user, so an account switch must not carry it over.
				resetIdentity: () => set({ ...initialIdentityState }),
				// A person's birth/present data and the computed dial live outside
				// the identity slice, so identity-only reset leaves them on screen
				// across an account switch. This wipes every person-derived field.
				resetForAccountSwitch: () =>
					set({
						...initialBirthInfo,
						...initialPresentInfo,
						...initialPlanetData,
						...initialSearchData,
						...initialUIState,
						...initialIdentityState,
					}),
			}),
			{
				name: "chart-store",
				// Persist ONLY the recents history. A reload deliberately opens on a
				// blank "now" form with no loaded identity — the input fields, the
				// committed ints, the chart label, and the loaded record refs all
				// fall back to their initial values, so the user starts fresh and
				// re-loads a chart explicitly from Recent charts (one click) or the
				// bank. `recents` is JSON-safe (ints/strings/floats), so it round-
				// trips exactly with no Date/ISO-string coercion.
				partialize: (state) => ({
					recents: state.recents,
				}),
			}
		),
		{ name: "chart-store" }
	)
);

// Selectors for optimized re-renders
export const useBirthInfo = () =>
	useChartStore((state) => ({
		birthDateTime: state.birthDateTime,
		longitude: state.longitude,
		latitude: state.latitude,
		place: state.place,
		birthDateTimeInfo: state.birthDateTimeInfo,
	}));

export const usePresentInfo = () =>
	useChartStore((state) => ({
		presentDateTime: state.presentDateTime,
		presentLongitude: state.presentLongitude,
		presentLatitude: state.presentLatitude,
		presentPlace: state.presentPlace,
		presentDateTimeInfo: state.presentDateTimeInfo,
	}));

export const usePlanetData = () =>
	useChartStore((state) => ({
		natalPlanets: state.natalPlanets,
		directedPlanets: state.directedPlanets,
		transitPlanets: state.transitPlanets,
	}));

export const useSearchData = () =>
	useChartStore((state) => ({
		searchTerm: state.searchTerm,
		searchedHalfsums: state.searchedHalfsums,
		searchedFormulas: state.searchedFormulas,
	}));
